import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatClientNumbers,
  type LicenceDto,
  type PortalRegistrationDto,
} from '@wally-reseller/shared';
import { Skeleton } from '@wally-reseller/ui/badge';
import { Button } from '@wally-reseller/ui/button';
import { ConfirmDialog } from '@wally-reseller/ui/dialog';
import { ErrorState } from '@wally-reseller/ui/empty-state';
import { Textarea } from '@wally-reseller/ui/input';
import { ArrowLeft, MessageSquareWarning } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '@/lib/api';
import { useFormatDate, useFormatMoney, useT } from '@/lib/i18n';
import { draftToInput, RegistrationFields, type RegistrationDraft } from './RegistrationFields';
import { useMe } from './Layout';
import { StageReport } from './StageReport';
import { StatusBadge } from './StatusBadge';

export function LeadPage() {
  const { id = '' } = useParams();
  const t = useT();
  const fmt = useFormatDate();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['registrations', id], queryFn: () => api.registration(id) });
  const [editing, setEditing] = useState<RegistrationDraft | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const me = useMe();

  const refresh = (r: PortalRegistrationDto) => {
    queryClient.setQueryData(['registrations', id], r);
    queryClient.invalidateQueries({ queryKey: ['registrations'], exact: true });
    queryClient.invalidateQueries({ queryKey: ['registration-counts'] });
  };
  const save = useMutation({
    mutationFn: (d: RegistrationDraft) => api.updateRegistration(id, draftToInput(d)),
    onSuccess: (r) => {
      refresh(r);
      setEditing(null);
    },
  });
  const askReason = useMutation({ mutationFn: () => api.requestReason(id), onSuccess: refresh });
  const withdraw = useMutation({
    mutationFn: () => api.withdraw(id),
    onSuccess: (r) => {
      refresh(r);
      setConfirmWithdraw(false);
    },
  });

  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-40 w-full" />;
  const r = query.data;
  const open = r.status === 'submitted' || r.status === 'evidence_requested';
  const pending = r.evidenceRequests.at(-1);

  return (
    <div className="max-w-3xl space-y-5">
      <Link
        to="/leads"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ArrowLeft className="size-3.5" />
        {t('portal.lead.back')}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{r.legalName}</h1>
          <div className="text-sm text-muted-foreground">{formatClientNumbers(r) || r.country}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t('portal.lead.submitted', { date: fmt(r.submittedAt), name: r.submittedBy })}
          </div>
          {r.contractSignedOn && (
            <div className="mt-1 text-sm font-medium text-success">
              {t('portal.lead.signedOn', { date: fmt(r.contractSignedOn) })}
            </div>
          )}
          {r.lastPaymentOn && (
            <div className="text-xs text-muted-foreground">
              {t('portal.lead.lastPayment', { date: fmt(r.lastPaymentOn) })}
            </div>
          )}
        </div>
        <StatusBadge status={r.clientStatus} />
      </div>

      {r.status === 'refused' && (
        <div className="rounded-md border border-border bg-background p-3 text-sm">
          <p className="font-medium text-danger">{t('portal.lead.refused')}</p>
          {r.refusalReason ? (
            <p className="mt-1">{t('portal.lead.reason', { reason: r.refusalReason })}</p>
          ) : r.reasonRequestedAt ? (
            <p className="mt-1 text-muted-foreground">
              {t('portal.lead.reason.asked', { date: fmt(r.reasonRequestedAt) })}
            </p>
          ) : (
            // Art. 2.4: Wally explains a refusal on request.
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              disabled={askReason.isPending}
              onClick={() => askReason.mutate()}
            >
              {t('portal.lead.reason.ask')}
            </Button>
          )}
        </div>
      )}

      {r.status === 'accepted' && me.data?.partner.salesByPartner && (
        <StageReport key={r.stageReport?.at ?? 'none'} registration={r} />
      )}

      {r.status === 'accepted' && <Commission registration={r} />}

      {r.evidenceRequests.map((e, i) => (
        <div key={i} className="space-y-2 rounded-md border border-note-border bg-note p-3 text-sm">
          <div className="flex items-center gap-2 font-medium">
            <MessageSquareWarning className="size-4" />
            {t('portal.lead.evidence.title')} · {fmt(e.requestedAt)}
          </div>
          <p className="whitespace-pre-wrap">{e.message}</p>
          {e.answer ? (
            <div className="border-l-2 border-border pl-3 text-muted-foreground">
              <div className="text-xs">
                {t('portal.lead.evidence.answered', { date: fmt(e.answeredAt!) })}
              </div>
              <p className="whitespace-pre-wrap">{e.answer}</p>
            </div>
          ) : (
            r.status === 'evidence_requested' &&
            e === pending && <EvidenceAnswer id={r.id} onDone={refresh} />
          )}
        </div>
      ))}

      {editing ? (
        <form
          className="space-y-4 rounded-lg border border-border bg-background p-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            save.mutate(editing);
          }}
        >
          <RegistrationFields draft={editing} onChange={setEditing} />
          {save.isError && <p className="text-sm text-danger">{t('portal.error.generic')}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              {t('portal.lead.save')}
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t('portal.lead.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4 rounded-lg border border-border bg-background p-4 text-sm">
          <section>
            <h2 className="font-medium">{t('portal.lead.relationship')}</h2>
            <p className="text-muted-foreground">
              {t('portal.lead.since', { since: r.relationship.since })}
            </p>
            <p className="mt-1 whitespace-pre-wrap">{r.relationship.description}</p>
          </section>
          {(r.contact.name || r.contact.email || r.contact.phone) && (
            <section>
              <h2 className="font-medium">{t('portal.lead.contact')}</h2>
              <p className="text-muted-foreground">
                {[r.contact.name, r.contact.email, r.contact.phone].filter(Boolean).join(' · ')}
              </p>
            </section>
          )}
          {open && (
            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              <Button
                variant="outline"
                onClick={() =>
                  setEditing({
                    legalName: r.legalName,
                    since: r.relationship.since,
                    description: r.relationship.description,
                    contactName: r.contact.name ?? '',
                    contactEmail: r.contact.email ?? '',
                    contactPhone: r.contact.phone ?? '',
                  })
                }
              >
                {t('portal.lead.edit')}
              </Button>
              <Button
                variant="ghost"
                className="text-danger"
                onClick={() => setConfirmWithdraw(true)}
              >
                {t('portal.lead.withdraw')}
              </Button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmWithdraw}
        onOpenChange={setConfirmWithdraw}
        title={t('portal.lead.withdraw')}
        description={t('portal.lead.withdraw.confirm')}
        confirmLabel={t('portal.lead.withdraw')}
        danger
        busy={withdraw.isPending}
        onConfirm={() => withdraw.mutate()}
      />
    </div>
  );
}

function EvidenceAnswer({
  id,
  onDone,
}: {
  id: string;
  onDone: (r: PortalRegistrationDto) => void;
}) {
  const t = useT();
  const [answer, setAnswer] = useState('');
  const send = useMutation({
    mutationFn: () => api.answerEvidence(id, { answer }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      <Textarea
        aria-label={t('portal.lead.evidence.answer')}
        placeholder={t('portal.lead.evidence.answer')}
        required
        minLength={2}
        rows={3}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        className="bg-background"
      />
      {send.isError && <p className="text-danger">{t('portal.error.generic')}</p>}
      <Button type="submit" size="sm" disabled={send.isPending}>
        {t('portal.lead.evidence.send')}
      </Button>
    </form>
  );
}

/** Art. 3.4: what Wally received from the lead and the commission on it. */
function Commission({ registration: r }: { registration: PortalRegistrationDto }) {
  const t = useT();
  const fmt = useFormatDate();
  const money = useFormatMoney();
  const lines = useQuery({
    queryKey: ['registrations', r.id, 'commission'],
    queryFn: () => api.registrationCommission(r.id),
  });
  return (
    <div className="space-y-4 rounded-lg border border-border bg-background p-4 text-sm">
      {r.licences.length > 0 && (
        <section>
          <h2 className="font-medium">{t('portal.lead.licences')}</h2>
          <ul className="text-muted-foreground">
            {r.licences.map((l, i) => (
              <li key={i}>
                <Licence licence={l} />
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-medium">{t('portal.lead.commission')}</h2>
          <span className="font-medium tabular-nums">
            {t('portal.lead.commission.total', { amount: money(r.commissionToDate) })}
          </span>
        </div>
        {lines.isError ? (
          <ErrorState error={lines.error} onRetry={() => lines.refetch()} />
        ) : !lines.data ? (
          <Skeleton className="h-16 w-full" />
        ) : lines.data.length === 0 ? (
          <p className="text-muted-foreground">{t('portal.lead.commission.none')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3 font-medium">{t('portal.lead.commission.col.date')}</th>
                  <th className="py-1 pr-3 font-medium">{t('portal.lead.commission.col.kind')}</th>
                  <th className="py-1 pr-3 font-medium max-sm:hidden">
                    {t('portal.lead.commission.col.invoice')}
                  </th>
                  <th className="py-1 pr-3 text-right font-medium">
                    {t('portal.lead.commission.col.base')}
                  </th>
                  <th className="py-1 text-right font-medium">
                    {t('portal.lead.commission.col.commission')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lines.data.map((l) => (
                  <tr key={l.id}>
                    <td className="whitespace-nowrap py-1.5 pr-3">{fmt(l.date)}</td>
                    <td className="py-1.5 pr-3">{t(`portal.lead.kind.${l.kind}`)}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground max-sm:hidden">
                      {l.invoiceNumber ?? '—'}
                    </td>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">
                      {money(l.base)}
                    </td>
                    <td className="whitespace-nowrap py-1.5 text-right tabular-nums">
                      {money(l.commission)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Licence({ licence: l }: { licence: LicenceDto }) {
  const t = useT();
  const money = useFormatMoney();
  const interval =
    l.interval === 'month' || l.interval === 'year'
      ? t(`portal.lead.interval.${l.interval}`)
      : null;
  return (
    <>
      {l.product}
      {l.quantity !== null &&
        ` — ${
          l.unit === null
            ? `× ${l.quantity}`
            : t(
                l.unit === 'dossiers'
                  ? 'portal.lead.licence.dossiers'
                  : 'portal.lead.licence.seats',
                {
                  n: String(l.quantity),
                },
              )
        }`}
      {l.amount !== null && ` — ${money(l.amount)}${interval ? ` / ${interval}` : ''}`}
    </>
  );
}
